import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";

const LANGUAGE_OPTIONS = [
  { code: "en", label: "EN", flagSrc: "https://flagcdn.com/24x18/gb.png", fullLabel: "English" },
  { code: "it", label: "IT", flagSrc: "https://flagcdn.com/24x18/it.png", fullLabel: "Italiano" },
  { code: "es", label: "ES", flagSrc: "https://flagcdn.com/24x18/es.png", fullLabel: "Español" },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const LanguageSelector = ({ inline = false, light = false }) => {
  const { i18n } = useTranslation();
  const getLangCode = (lang) => {
    if (lang?.startsWith("it")) return "it";
    if (lang?.startsWith("es")) return "es";
    return "en";
  };

  const [activeLang, setActiveLang] = useState(() => getLangCode(i18n.language));
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    setActiveLang(getLangCode(i18n.language));
  }, [i18n.language]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = async (code) => {
    setOpen(false);
    if (activeLang === code) return;
    setActiveLang(code);
    await i18n.changeLanguage(code);
    localStorage.setItem("betterhusbandUserLang", code);
    try {
      localStorage.setItem("i18nextLng", code);
    } catch (_) { /* ignore quota errors */ }
    // Persist to the user profile on the backend so the choice survives
    // localStorage clears, app reinstalls, and login on other devices.
    const userId = localStorage.getItem("betterhusbandUserId");
    if (userId) {
      try {
        await axios.put(`${API}/user/${userId}`, { language: code });
      } catch (err) {
        // Non-blocking: UserContext will retry sync on next load.
        console.warn("[LanguageSelector] Failed to persist language to backend:", err?.message || err);
      }
    }
  };

  const current = LANGUAGE_OPTIONS.find((o) => o.code === activeLang) || LANGUAGE_OPTIONS[0];

  return (
    <div
      ref={containerRef}
      style={{ position: inline ? "relative" : "absolute", top: inline ? undefined : 12, right: inline ? undefined : 12, zIndex: 30 }}
    >
      {/* Trigger button */}
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 10px",
          borderRadius: 999,
          border: light ? "1px solid rgba(0,0,0,0.55)" : "1px solid rgba(255,255,255,0.35)",
          background: light ? "transparent" : "rgba(0,0,0,0.55)",
          backdropFilter: light ? undefined : "blur(6px)",
          cursor: "pointer",
          color: light ? "#1a1a1a" : "#fff",
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: "0.02em",
          userSelect: "none",
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <img
          src={current.flagSrc}
          alt={current.fullLabel}
          style={{ width: 20, height: 15, borderRadius: 2, objectFit: "cover" }}
        />
        <span>{current.label}</span>
        <svg width="10" height="6" viewBox="0 0 10 6" fill="none" style={{ marginLeft: 2, opacity: 0.8, transition: "transform 0.15s", transform: open ? "rotate(180deg)" : "rotate(0deg)" }}>
          <path d="M1 1l4 4 4-4" stroke={light ? "#1a1a1a" : "white"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          role="listbox"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            background: "#fff",
            borderRadius: 12,
            boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            overflow: "hidden",
            minWidth: 130,
            zIndex: 100,
          }}
        >
          {LANGUAGE_OPTIONS.map((opt) => (
            <button
              key={opt.code}
              role="option"
              aria-selected={activeLang === opt.code}
              onClick={() => handleSelect(opt.code)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                width: "100%",
                padding: "10px 14px",
                border: "none",
                background: activeLang === opt.code ? "#f5f5f0" : "transparent",
                cursor: "pointer",
                fontSize: 14,
                fontWeight: activeLang === opt.code ? 700 : 500,
                color: "#1a1a1a",
                textAlign: "left",
              }}
            >
              <img
                src={opt.flagSrc}
                alt={opt.fullLabel}
                style={{ width: 20, height: 15, borderRadius: 2, objectFit: "cover" }}
              />
              <span>{opt.fullLabel}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
